import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVerticalIcon, CalendarIcon, CheckIcon, XIcon, AlertTriangleIcon } from 'lucide-react';
import { Schedule } from '../../types/schedule';
import Button from '../common/Button';
import Modal from '../common/Modal';
import DatePicker from '../common/DatePicker';
import { addDays } from 'date-fns';
import { toast } from 'react-hot-toast';

interface ScheduleActionsProps {
  schedule: Schedule;
  onSuccess?: () => void;
}

const ScheduleActions: React.FC<ScheduleActionsProps> = ({ schedule, onSuccess }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [isClosePreferencesModalOpen, setIsClosePreferencesModalOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [preferencesDueDate, setPreferencesDueDate] = useState<Date>(
    addDays(new Date(), 7)
  );
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  
  const openScheduleForPreferences = async (scheduleId: string, dueDate: string) => {
    setIsLoading(true);
    try {
      // Mock implementation - in a real app, this would call an API
      // Replace with actual API call to open a schedule for preferences
      console.log(`Opening schedule ${scheduleId} for preferences with due date ${dueDate}`);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return true;
    } catch (error) {
      console.error('Error opening preferences:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  const closeSchedulePreferences = async (scheduleId: string) => {
    setIsLoading(true);
    try {
      // Mock implementation - in a real app, this would call an API
      // Replace with actual API call to close preferences for a schedule
      console.log(`Closing preferences for schedule ${scheduleId}`);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return true;
    } catch (error) {
      console.error('Error closing preferences:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  const publishScheduleToTeam = async (scheduleId: string) => {
    setIsLoading(true);
    try {
      // Mock implementation - in a real app, this would call an API
      // Replace with actual API call to publish a schedule
      console.log(`Publishing schedule ${scheduleId}`);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return true;
    } catch (error) {
      console.error('Error publishing schedule:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleOpenPreferences = async () => {
    if (!preferencesDueDate) {
      toast.error('Please select a due date for preferences');
      return;
    }
    
    try {
      await openScheduleForPreferences(
        schedule.id,
        preferencesDueDate.toISOString()
      );
      toast.success('Schedule opened for preferences');
      setIsPreferencesModalOpen(false);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error opening preferences:', error);
      toast.error('Failed to open schedule for preferences');
    }
  };
  
  const handleClosePreferences = async () => {
    try {
      await closeSchedulePreferences(schedule.id);
      toast.success('Preferences closed successfully');
      setIsClosePreferencesModalOpen(false);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error closing preferences:', error);
      toast.error('Failed to close preferences');
    }
  };
  
  const handlePublishSchedule = async () => {
    try {
      await publishScheduleToTeam(schedule.id);
      toast.success('Schedule published successfully');
      setIsPublishModalOpen(false);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error publishing schedule:', error);
      toast.error('Failed to publish schedule');
    }
  };
  
  // Define available actions based on current schedule status
  const actions = [];
  
  // Draft -> Open Preferences
  if (schedule.status === 'draft') {
    actions.push({
      label: 'Open for Preferences',
      icon: CalendarIcon,
      onClick: () => setIsPreferencesModalOpen(true),
    });
  }
  
  // Preferences Open -> Close Preferences
  if (schedule.status === 'preferences_open') {
    actions.push({
      label: 'Close Preferences',
      icon: XIcon,
      onClick: () => setIsClosePreferencesModalOpen(true),
    });
  }
  
  // Preferences Closed -> Publish
  if (schedule.status === 'preferences_closed') {
    actions.push({
      label: 'Publish Schedule',
      icon: CheckIcon,
      onClick: () => setIsPublishModalOpen(true),
    });
  }
  
  return (
    <>
      <div className="relative">
        <Button
          variant="secondary"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center"
        >
          <span>Actions</span>
          <MoreVerticalIcon size={16} className="ml-2" />
        </Button>
        
        {isDropdownOpen && (
          <div
            className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
            onMouseLeave={() => setIsDropdownOpen(false)}
          >
            <div className="py-1">
              {actions.map((action, index) => (
                <button
                  key={index}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => {
                    setIsDropdownOpen(false);
                    action.onClick();
                  }}
                >
                  <action.icon size={16} className="mr-2" />
                  {action.label}
                </button>
              ))}
              
              {/* Always show the Edit action */}
              <button
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate(`/schedules/${schedule.id}/edit`);
                }}
              >
                <CalendarIcon size={16} className="mr-2" />
                Edit Schedule
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Open Preferences Modal */}
      <Modal
        isOpen={isPreferencesModalOpen}
        onClose={() => setIsPreferencesModalOpen(false)}
        title="Open Schedule for Preferences"
      >
        <div className="p-4">
          <p className="mb-4 text-gray-600">
            Providers will be able to submit their preferred shifts and request days off until the due date.
          </p>
          
          <div className="my-4">
            <DatePicker
              label="Preferences Due Date"
              selected={preferencesDueDate}
              onChange={(date) => setPreferencesDueDate(date as Date)}
              minDate={addDays(new Date(), 1)}
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={() => setIsPreferencesModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleOpenPreferences}
            isLoading={isLoading}
          >
            Open for Preferences
          </Button>
        </div>
      </Modal>
      
      {/* Close Preferences Modal */}
      <Modal
        isOpen={isClosePreferencesModalOpen}
        onClose={() => setIsClosePreferencesModalOpen(false)}
        title="Close Preferences"
      >
        <div className="p-4">
          <div className="flex items-start mb-4">
            <div className="mr-3 text-yellow-500">
              <AlertTriangleIcon size={24} />
            </div>
            <div>
              <p className="text-gray-600">
                Are you sure you want to close the preference submission period? Providers will no longer be able to submit or modify their preferences.
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={() => setIsClosePreferencesModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleClosePreferences}
            isLoading={isLoading}
          >
            Close Preferences
          </Button>
        </div>
      </Modal>
      
      {/* Publish Schedule Modal */}
      <Modal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        title="Publish Schedule"
      >
        <div className="p-4">
          <div className="flex items-start mb-4">
            <div className="mr-3 text-green-500">
              <CheckIcon size={24} />
            </div>
            <div>
              <p className="text-gray-600">
                Are you sure you want to publish this schedule? This will make the schedule visible to all team members and notify them.
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={() => setIsPublishModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handlePublishSchedule}
            isLoading={isLoading}
          >
            Publish Schedule
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ScheduleActions; 